// This is a fetch with the special Vercel skew protection headers
// import "./utils/global-fetch";

// Universal Logger — local-dev only, tree-shaken in production.
// Runs BEFORE any other module so its console patcher captures startup logs.
if (import.meta.env.DEV) {
  await import('./dev/install-universal-logger');
}
// Side-effect import: parks the Zustand stores on `window.__flashka` so the
// `web_eval` MCP tool can inspect state — and so the E2E fixture seeding can
// write through the app's own sync client. Same gate as the devtools: local
// dev AND Vercel PREVIEW builds, never production. A PR's hosted preview is
// what the cloud device runs seed against, and without the bridge every
// seeded mode failed (`bridge:false`, run 39860044558).
//
// NOT awaited: the build targets safari12, which has no top-level await, and
// unlike the DEV branch above this one survives into preview builds — an
// `await` here failed the Vercel preview build. Nothing needs it before the
// app mounts; the seeding polls for `window.__flashka.sync` (`waitForSyncReady`).
if (import.meta.env.DEV || __DEVTOOLS_ENABLED__) {
  void import('./dev/expose-stores');
}

import './index.css';
import './styles/fonts.css';
import './i18n'; // Initialise i18n before any other module evaluates

import { releaseBootLogoLoader } from '@flashka/shared/ui';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import App from './App.tsx';
import { ImpersonateConfirm } from './auth/better/components/impersonate-confirm';
import { readImpersonationHandoff } from './auth/better/impersonation-handoff';
import { captureFtueOverrideFromUrl } from './components/ftue/use-ftue-v2';
import { i18nReady } from './i18n';
import { installDomMutationGuard } from './lib/dom-mutation-guard';
import { installFileDropGuard } from './lib/file-drop';
import { initWebSounds } from './lib/sound';
import { stripStaticSeoHead } from './metadata/strip-static-seo-head';
import { registerPWA } from './pwa';
import { isLandingShellPath } from './shell-paths';

// FTUE v2 preview links (`?ftue=v2`) survive the auth/onboarding redirects.
captureFtueOverrideFromUrl();

// Must run before React mounts: makes removeChild/insertBefore tolerant of
// Google-Translate-style external DOM rewrites that otherwise crash the app
// (NotFoundError in the commit phase). See lib/dom-mutation-guard.ts.
installDomMutationGuard();
// A file dropped where nothing takes it must not make the browser open it and
// leave the app. Ungated: landing and onboarding lose state too. See
// lib/file-drop.tsx.
installFileDropGuard();

// index.html's boot loader (the shuffling mark) stays up until either root
// below draws something real; then it bows out (see releaseBootLogoLoader).
// On the landing the mark flies into the nav's own logo instead of vanishing.
releaseBootLogoLoader(document.getElementById('root')!, {
  flyToLogo: isLandingShellPath(window.location.pathname),
});

// /impersonate#token=… from the auth worker's admin UI. The app does NOT
// mount for it: no auth provider may observe the pre-handoff session, and the
// token in the fragment is an untrusted claim until the worker has vouched for
// it AND a human has confirmed the account it names. A dedicated root renders
// that gate instead — see auth/better/components/impersonate-confirm.tsx.
// Statically imported, not lazily: `build.target` is safari12, which has no
// top-level await, and the gate's whole module graph (../client, the handoff)
// is already in this entry anyway.
const impersonationHandoff = readImpersonationHandoff();
if (impersonationHandoff) {
  // Both roots render once the page's language has loaded — English is in the
  // entry, every other language is a chunk of its own (src/i18n.ts).
  void i18nReady.then(() =>
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <ImpersonateConfirm token={impersonationHandoff.token} />
      </StrictMode>,
    ),
  );
} else {
  registerPWA();

  // UI sound effects (shared cross-platform registry). Safe pre-mount: the
  // AudioContext unlocks itself on the first user gesture.
  initWebSounds();

  // Remove the static/edge-injected crawler SEO fallback from <head> before React
  // renders, so React owns a single, correct set of per-page meta tags (no
  // duplicate <title>/canonical for JS clients).
  stripStaticSeoHead();

  void i18nReady.then(() =>
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <App />
      </StrictMode>,
    ),
  );
}
